import { isEqual } from 'es-toolkit/compat'
import type { FlashMessage, FlashMessagesResponse } from '#types'

type VALID_FLASH_MESSAGE = Partial<Pick<FlashMessage, 'element' | 'message' | 'key' | 'detached_key'>>

const VALID_FLASH_MESSAGES: Array<VALID_FLASH_MESSAGE> = [
    {
        element: 'Flash/success',
        message: 'The mobile phone number has been confirmed. You are now registered for SMS marketing.',
        key: 'flash',
    },
    {
        element: 'Flash/success',
        message: 'The mobile phone number has been confirmed. You are now registered for SMS marketing. Your bonus is ready. Please log in to redeem it.',
        key: 'flash',
    },
    {
        detached_key: 'genericExit',
    },
    {
        element: 'Flash/error',
        key: 'flash',
        message: 'An error occurred. Try again or contact support.',
    },
    {
        element: 'Flash/bottom_scroll_dismiss',
        key: 'flash',
    },
    {
        element: 'Flash/error',
        message: 'Invalid link. Try copying and pasting the entire link in your web browser.'
    },
    {
        element: 'Flash/error',
        message: 'The link is either invalid or has expired or your password has already been reset.'
    },
    {
        element: 'Flash/success',
        message: 'Your password has been changed.'
    }
] as const

/**
 * Legacy way of getting notifications / flash messages.
 * To be replaced by message service in the future.
 */
export const useFlashMessages = () => {
    const api = useApi({ enableLegacySupport: true, service: 'monolith' })

    const query = useQuery<FlashMessagesResponse>(async () => {
        const response = await api<FlashMessagesResponse>('/flash', {
            method: 'POST',
        })

        // Filter flash messages so we only keep the ones we want to show
        if (response.flash.length) {
            /**
             * Flash registry that prevents showing duplicate flashes.
             */
            const seenFlashes: FlashMessage[] = []

            response.flash = response.flash.filter((flash) => {
                // Filter out duplicates
                if (seenFlashes.find(seenFlash => isEqual(seenFlash, flash))) return false

                // Only show valid flash messages
                const isValid = VALID_FLASH_MESSAGES.some((validFlashMessage) => {
                    if (validFlashMessage.element && validFlashMessage.element !== flash.element) return false

                    if (validFlashMessage.key && validFlashMessage.key !== flash.key) return false

                    if (validFlashMessage.detached_key && validFlashMessage.detached_key !== flash.detached_key) return false

                    if (validFlashMessage.message && !isEqual(validFlashMessage.message, flash.message)) return false

                    return true
                })

                if (isValid) {
                    // Add to seen flashes to avoid duplicates
                    seenFlashes.push(flash)

                    return true
                }

                return false
            })
        }

        return response
    }, {
        key: 'monolith:flash-messages',
        queryCache: false,
        server: false,
        lazy: true,
    })

    return query
}
